/* ============================================================
   REWARD STUDIO — Theme JavaScript
   Handles: gift card balance lookup, cart updates, UI behaviors
   ============================================================ */

// Prevent smooth-scroll animation on initial page load when the URL has a hash.
// Without this, navigating to /collections/{handle}/{tag}#products animates a
// visible scroll from page top down to #products. We want the initial anchor
// jump to be instant, then restore smooth scroll for in-page anchor clicks.
(function preventInitialSmoothScroll() {
  if (window.location.hash && document.documentElement) {
    var html = document.documentElement;
    var original = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.addEventListener('load', function () {
      setTimeout(function () { html.style.scrollBehavior = original || ''; }, 100);
    });
  }
})();

const RewardStudio = {

  /* ===== Gift card balance state ===== */
  giftCard: {
    save(data) {
      try { localStorage.setItem('rs_gc', JSON.stringify(data)); } catch(e) {}
    },
    get() {
      try { return JSON.parse(localStorage.getItem('rs_gc')); } catch(e) { return null; }
    },
    clear() {
      try { localStorage.removeItem('rs_gc'); } catch(e) {}
      // Proactively strip the card from the cart so a cleared/replaced card can
      // never ride along to checkout (#133). Fire-and-forget — never block the UI.
      try {
        if (RewardStudio.cart && RewardStudio.cart.detachGiftCards) {
          RewardStudio.cart.detachGiftCards();
        }
      } catch(e) {}
    }
  },

  /* ===== Look up a gift card balance =====
     Shopify exposes gift card info during checkout. Outside of checkout,
     the cleanest path is to apply the code to the cart (which validates it
     and exposes the balance), then optionally remove if invalid.
     This uses Shopify's /cart/update endpoint with the gift card code.
  */
  async lookupGiftCard(code) {
    // Clean code: strip dashes, spaces, em-dashes
    const cleaned = code.replace(/[\s\-—]/g, '').toUpperCase();
    if (cleaned.length < 8) {
      throw new Error('Code must be at least 8 characters');
    }

    // Toggle between simulation and real App Proxy lookup based on the theme
    // setting `simulate_gift_card_lookup` (Theme editor → Theme settings → Gift
    // Card Lookup). Default is TRUE (simulation) so development works without
    // the backend. Turn OFF once the Worker at /apps/giftcard/lookup is deployed
    // (see APP-DEV-GIFTCARD.md and the Worker-Giftcard/ folder at project root).
    var simulate = true;
    try {
      if (window.RewardStudio && window.RewardStudio.config && window.RewardStudio.config.simulateGiftCard === false) {
        simulate = false;
      }
    } catch (e) { /* default to simulation */ }

    if (simulate) {
      return this._simulateLookup(cleaned);
    }

    // Real lookup via App Proxy. The backend validates the code against
    // Shopify Admin GraphQL `giftCards` query, signs the response shape to
    // match `_simulateLookup`, and returns balance + last4 + expiry.
    const response = await fetch('/apps/giftcard/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: cleaned })
    });

    if (!response.ok) {
      const err = await response.json().catch(function () { return {}; });
      throw new Error(err.error || 'Could not verify code. Please check and try again.');
    }

    return response.json();
  },

  _simulateLookup(code) {
    // Simulated balance for testing — replace with real API response via App Proxy.
    // Test value $25 so cart-minimum enforcement (cart total must exceed card balance)
    // is easy to demonstrate with one or two product purchases.
    const last4 = code.slice(-4);
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (code === 'INVALID' || code.startsWith('0000')) {
          reject(new Error('That code is not valid or has been used.'));
        } else {
          resolve({
            code: code,
            last4: last4,
            balance: 2500,
            balance_formatted: '$25.00',
            currency: 'USD',
            expires_on: '2027-03-31',
            expires_formatted: 'Mar 31, 2027'
          });
        }
      }, 600);
    });
  },

  /* ===== Money formatter ===== */
  formatMoney(cents, currency) {
    currency = currency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  },

  /* ===== Update the persistent nav balance pill ===== */
  updateBalancePill() {
    const data = this.giftCard.get();
    const pill = document.querySelector('[data-balance-pill]');
    if (!pill) return;
    if (data && data.balance) {
      pill.removeAttribute('hidden');
      const amount = pill.querySelector('[data-balance-amount]');
      if (amount) amount.textContent = data.balance_formatted;
    } else {
      pill.setAttribute('hidden', '');
    }
  },

  /* ===== Cart helpers ===== */
  cart: {
    async add(variantId, quantity) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] })
      });
      if (!response.ok) throw new Error('Could not add to cart');
      return response.json();
    },

    async remove(line) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: 0 })
      });
      if (!response.ok) throw new Error('Could not remove item');
      return response.json();
    },

    async update(line, quantity) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      });
      if (!response.ok) throw new Error('Could not update item');
      return response.json();
    },

    // Persist arbitrary key/value pairs on the cart object. Cart attributes
    // survive across pages and into the checkout context — used by the
    // Checkout UI Extension (Workstream 3) to surface the gift card balance
    // as a branded "Reward Studio Credit applied" banner without the customer
    // re-entering anything.
    async setAttributes(attributes) {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      });
      if (!response.ok) throw new Error('Could not update cart attributes');
      return response.json();
    },

    // The end-to-end "pass through" handler. Tries paths in priority order:
    //
    //   1. Storefront API `cartGiftCardCodesUpdate` mutation (preferred,
    //      Plus-grade). Requires `window.RewardStudio.config.storefrontApiToken`
    //      to be provisioned in Theme settings → Gift card lookup. Persistently
    //      attaches the gift card to the cart object so the hosted checkout
    //      shows it as already-applied, and Workstream 3's Checkout UI
    //      Extension can read it via `appliedGiftCards`.
    //   2. Cart attributes + Checkout UI Extension. Even when the Storefront
    //      API path is unavailable, we always write the code + balance to cart
    //      attributes. Once the Reward Studio Checkout UI Extension is deployed
    //      (Workstream 3 in Shopify-App/), it reads those attributes on the
    //      hosted checkout page and calls applyGiftCardChange() to apply the
    //      code from inside the checkout context — invisible to the customer.
    //   3. Clipboard-copy fallback. When neither (1) nor (2) is active yet —
    //      e.g. during the rollout before the Storefront token is provisioned
    //      AND the extension is deployed — copy the code to the clipboard and
    //      navigate to checkout. The customer pastes manually into the "Gift
    //      card or discount code" field. A toast confirms the copy.
    //
    // IMPORTANT: the previous implementation used /discount/<CODE>?redirect=
    // /checkout as a fallback. That permalink ONLY handles discount codes,
    // not gift cards — they share the same checkout field UI but the URL
    // routes to the discount-code resolver. Gift card codes pass through
    // unrecognized. Removed to avoid the silent "arrived at checkout, nothing
    // applied" failure mode.
    async checkoutWithGiftCard(checkoutUrl) {
      const card = (typeof RewardStudio !== 'undefined' && RewardStudio.giftCard)
        ? RewardStudio.giftCard.get() : null;
      const defaultUrl = checkoutUrl || '/checkout';
      if (!card || !card.code) {
        window.location.href = defaultUrl;
        return;
      }

      // Always write to cart attributes regardless of which apply-path wins.
      // Provides a durable record of the credit context to the Checkout UI
      // Extension and to support; cheap fire-and-forget. Errors here don't
      // block the checkout navigation.
      try {
        await this.setAttributes({
          'Reward Studio Credit Code': card.code,
          'Reward Studio Credit Balance': card.balance_formatted || ''
        });
      } catch (_e) { /* non-blocking */ }

      // Path 1: Storefront API (if a token has been provisioned in theme settings)
      const sfToken = (window.RewardStudio && window.RewardStudio.config && window.RewardStudio.config.storefrontApiToken) || '';
      if (sfToken) {
        try {
          await this._attachGiftCardViaStorefront(card.code, sfToken);
          window.location.href = '/checkout';
          return;
        } catch (e) {
          // Log and fall through. Don't block checkout because of a
          // Storefront API hiccup — the customer can still complete purchase
          // via the clipboard-fallback path below.
          console.warn('[RewardStudio] Storefront cartGiftCardCodesUpdate failed; falling back to clipboard', e);
        }
      }

      // Path 2/3: cart attribute (already written above) + clipboard copy.
      // The Checkout UI Extension picks up the cart attribute and auto-applies
      // on checkout. If the extension isn't deployed yet, the clipboard copy
      // lets the customer paste manually. Single line of defense covers both
      // scenarios — the toast tells the customer what to do either way.
      this._copyCodeAndCheckout(card, defaultUrl);
    },

    // Copy the gift card code to the clipboard, show a toast, then redirect
    // to checkout after a brief delay so the toast is visible. Used as the
    // last-resort fallback in checkoutWithGiftCard().
    _copyCodeAndCheckout(card, checkoutUrl) {
      const reward = (typeof RewardStudio !== 'undefined') ? RewardStudio : null;
      const go = function () {
        setTimeout(function () { window.location.href = checkoutUrl; }, 1100);
      };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(card.code).then(function () {
            if (reward && reward.toast) reward.toast('Gift card code copied — paste at the discount field if not auto-applied.', 'success');
            go();
          }).catch(function () {
            if (reward && reward.toast) reward.toast('Your gift card code: ' + card.code, 'info');
            go();
          });
        } else {
          if (reward && reward.toast) reward.toast('Your gift card code: ' + card.code, 'info');
          go();
        }
      } catch (_e) {
        if (reward && reward.toast) reward.toast('Your gift card code: ' + card.code, 'info');
        go();
      }
    },

    // Storefront API mutation helper — used by checkoutWithGiftCard() when a
    // public Storefront API token is configured. Resolves on success or throws
    // on user-error / network failure (caller falls back to the permalink).
    async _attachGiftCardViaStorefront(code, sfToken) {
      // Fetch the AJAX cart to get the token; Storefront API cart IDs are
      // constructed from this token on Plus-with-unified-cart stores.
      const cartResp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResp.ok) throw new Error('Could not read /cart.js');
      const cart = await cartResp.json();
      if (!cart || !cart.token) throw new Error('Cart token missing');
      const cartId = 'gid://shopify/Cart/' + cart.token;
      const desiredLast4 = String(code).replace(/[\s\-—]/g, '').slice(-4).toUpperCase();

      // Minimal Storefront GraphQL helper (same token + version for every call).
      const gql = async function (query, variables) {
        const r = await fetch('/api/2024-10/graphql.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': sfToken },
          body: JSON.stringify({ query: query, variables: variables })
        });
        if (!r.ok) throw new Error('Storefront API HTTP ' + r.status);
        return r.json();
      };

      // #133 — A new card lookup must REPLACE any previously-applied card, never
      // stack with it. cartGiftCardCodesUpdate does NOT remove already-applied
      // gift cards (those persist in cart.appliedGiftCards), so we explicitly:
      //   1) read what's applied, 2) remove every card that isn't this one,
      //   3) apply this one only if it isn't already attached.

      // 1. Currently-applied gift cards.
      const curResp = await gql(
        'query($cartId: ID!) { cart(id: $cartId) { appliedGiftCards { id lastCharacters } } }',
        { cartId: cartId }
      );
      const appliedNow = (curResp.data && curResp.data.cart && curResp.data.cart.appliedGiftCards) || [];

      // 2. Remove any applied card whose last 4 differ from the one we're attaching.
      const staleIds = appliedNow
        .filter(function (g) { return (g.lastCharacters || '').toUpperCase() !== desiredLast4; })
        .map(function (g) { return g.id; });
      if (staleIds.length > 0) {
        const rmResp = await gql(
          'mutation($cartId: ID!, $ids: [ID!]!) { cartGiftCardCodesRemove(cartId: $cartId, appliedGiftCardIds: $ids) { cart { id } userErrors { field message } } }',
          { cartId: cartId, ids: staleIds }
        );
        const rmErrs = (rmResp.data && rmResp.data.cartGiftCardCodesRemove && rmResp.data.cartGiftCardCodesRemove.userErrors) || [];
        if (rmErrs.length > 0) throw new Error('Storefront remove userErrors: ' + JSON.stringify(rmErrs));
      }

      // 3. Apply the desired card only if it isn't already on the cart.
      const alreadyApplied = appliedNow.some(function (g) { return (g.lastCharacters || '').toUpperCase() === desiredLast4; });
      if (!alreadyApplied) {
        const upResp = await gql(
          'mutation($cartId: ID!, $codes: [String!]!) { cartGiftCardCodesUpdate(cartId: $cartId, giftCardCodes: $codes) { cart { id checkoutUrl } userErrors { field message } } }',
          { cartId: cartId, codes: [code] }
        );
        const upErrs = (upResp.data && upResp.data.cartGiftCardCodesUpdate && upResp.data.cartGiftCardCodesUpdate.userErrors) || [];
        if (upErrs.length > 0) throw new Error('Storefront userErrors: ' + JSON.stringify(upErrs));
        return upResp.data.cartGiftCardCodesUpdate.cart;
      }
      return { id: cartId };
    },

    // Detach the Reward Studio gift card from the cart (#133). Called when the
    // customer clears/replaces their card (giftCard.clear → resetGiftCardLookup)
    // so a removed card can't silently ride along to checkout. Best-effort and
    // non-blocking: clears the RS cart attributes (so the Checkout UI Extension
    // won't re-apply), then — if a Storefront API token is provisioned — strips
    // every applied gift card from the cart object.
    async detachGiftCards() {
      // 1. Clear the RS cart attributes regardless of Storefront token presence.
      try {
        await this.setAttributes({ 'Reward Studio Credit Code': '', 'Reward Studio Credit Balance': '' });
      } catch (e) { /* non-blocking */ }

      const sfToken = (window.RewardStudio && window.RewardStudio.config && window.RewardStudio.config.storefrontApiToken) || '';
      if (!sfToken) return;

      try {
        const cartResp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartResp.ok) return;
        const cart = await cartResp.json();
        if (!cart || !cart.token) return;
        const cartId = 'gid://shopify/Cart/' + cart.token;
        const gql = async function (query, variables) {
          const r = await fetch('/api/2024-10/graphql.json', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': sfToken },
            body: JSON.stringify({ query: query, variables: variables })
          });
          if (!r.ok) throw new Error('Storefront API HTTP ' + r.status);
          return r.json();
        };
        const cur = await gql('query($cartId: ID!) { cart(id: $cartId) { appliedGiftCards { id } } }', { cartId: cartId });
        const ids = ((cur.data && cur.data.cart && cur.data.cart.appliedGiftCards) || []).map(function (g) { return g.id; });
        if (ids.length > 0) {
          await gql('mutation($cartId: ID!, $ids: [ID!]!) { cartGiftCardCodesRemove(cartId: $cartId, appliedGiftCardIds: $ids) { cart { id } userErrors { field message } } }', { cartId: cartId, ids: ids });
        }
      } catch (e) { /* best-effort; never block the UI */ }
    }
  },

  /* ===== Toast notification ===== */
  toast(message, type) {
    const t = document.createElement('div');
    t.className = 'rs-toast';
    t.textContent = message;
    t.style.cssText = `
      position: fixed; bottom: 2rem; left: 50%;
      transform: translateX(-50%); z-index: 9999;
      background: ${type === 'error' ? '#8a2a1f' : 'var(--ink)'};
      color: white; padding: 0.85rem 1.5rem;
      border-radius: 999px; font-size: 0.85rem;
      font-family: var(--font-sans);
      box-shadow: 0 8px 24px rgba(0,0,0,0.15);
      animation: rsToastIn 0.3s, rsToastOut 0.3s 2.7s forwards;
    `;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 3000);
  }
};

// Add keyframes for toast
(function() {
  const s = document.createElement('style');
  s.textContent = `
    @keyframes rsToastIn { from { opacity: 0; transform: translate(-50%, 10px); } to { opacity: 1; transform: translate(-50%, 0); } }
    @keyframes rsToastOut { to { opacity: 0; transform: translate(-50%, -10px); } }
  `;
  document.head.appendChild(s);
})();

// Auto-format gift card code input as XXXX — XXXX — XXXX — XXXX
document.addEventListener('input', function(e) {
  if (e.target.matches('[data-gift-card-input]')) {
    let val = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    let formatted = val.match(/.{1,4}/g)?.join(' — ') || val;
    e.target.value = formatted;
  }
});

// Initialize balance pill on every page load
document.addEventListener('DOMContentLoaded', function() {
  RewardStudio.updateBalancePill();
  RewardStudio.applyGiftCardToPrices();
  RewardStudio.applyBxgyEligibility();
  RewardStudio.reconcileBxgyFreeItems();
});

// Buy X Get Y auto-targeting (#148 Worker phase). When enabled in Theme settings,
// fetch the active automatic BXGY discount scope from the gift-card Worker and
// reveal the banner/badge on qualifying products. Banner/badge elements render
// server-side with [hidden] (unless already tag-revealed) + data-bxgy-check +
// data-bxgy-product-id + data-bxgy-collection-ids; this removes [hidden] for any
// element whose product is in the discount's scope. If the Worker is unreachable,
// the tag-revealed elements still show (graceful fallback).
RewardStudio.applyBxgyEligibility = function () {
  var cfg = this.config && this.config.bxgyWorker;
  if (!cfg || !cfg.enabled) return;
  var checks = document.querySelectorAll('[data-bxgy-check]');
  if (!checks.length) return;
  var TOKEN = '{bonus}';
  var fallback = cfg.fallbackLabel || '';

  // Replace {bonus} in every text node under `el` (preserves markup; only text).
  function setToken(el, value) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var node, list = [];
    while ((node = walker.nextNode())) {
      if (node.nodeValue.indexOf(TOKEN) !== -1) list.push(node);
    }
    list.forEach(function (n) { n.nodeValue = n.nodeValue.split(TOKEN).join(value); });
  }

  // Find the title of the first discount whose scope covers this product.
  function matchTitle(discounts, pid, cids) {
    for (var i = 0; i < discounts.length; i++) {
      var d = discounts[i];
      if (d.allItems === true) return d.title;
      if (pid && (d.productIds || []).indexOf(pid) !== -1) return d.title;
      var dc = d.collectionIds || [];
      for (var j = 0; j < cids.length; j++) {
        if (cids[j] && dc.indexOf(cids[j]) !== -1) return d.title;
      }
    }
    return null;
  }

  fetch(cfg.endpoint, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      var discounts = (data && data.discounts) || [];
      checks.forEach(function (el) {
        var pid = String(el.getAttribute('data-bxgy-product-id') || '');
        var cids = (el.getAttribute('data-bxgy-collection-ids') || '').split(',');
        var title = matchTitle(discounts, pid, cids);
        if (title) {
          el.removeAttribute('hidden');     // Worker says this product qualifies
          setToken(el, title);              // {bonus} → the matched discount's title
        } else if (!el.hasAttribute('hidden')) {
          // Visible via tag/metafield fallback but no discount matched — resolve
          // {bonus} to the store-wide label so no literal token ever shows.
          setToken(el, fallback);
        }
      });
    })
    .catch(function () {
      // Worker unreachable — tag-revealed elements still show; just make sure no
      // literal {bonus} token is left visible.
      checks.forEach(function (el) { if (!el.hasAttribute('hidden')) setToken(el, fallback); });
    });
};

// Buy X Get Y free-item cart automation (#148, cart phase). On the CART PAGE,
// ensure the free "get" product(s) are present at the correct quantity and
// removed when they shouldn't be — the customer can't add them manually.
//
// SAFETY: the free item is only added when the discount's BUY CONDITION is met
// (min spend / min qty), because Shopify only zeroes the free item when the
// discount actually applies — adding it otherwise would CHARGE the customer.
// Quantity = min(eligible buy units, max uses per order). Shopify's automatic
// discount does the actual $0 pricing; this only manages presence/qty.
RewardStudio.reconcileBxgyFreeItems = async function () {
  var self = this;
  var cfg = this.config && this.config.bxgyWorker;
  if (!cfg || !cfg.enabled) return;
  // Cart page only (body.template-cart, set by theme.liquid).
  if (!document.body || !document.body.classList.contains('template-cart')) return;

  try {
    var data = await fetch(cfg.endpoint, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; });
    var discounts = (data && data.discounts) || [];
    if (!discounts.length) return;

    var cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
    var items = cart.items || [];

    // All free "get" variant IDs — excluded from eligible counting, and the set
    // we reconcile (any not wanted gets zeroed out).
    var allGets = {};
    discounts.forEach(function (d) {
      (d.getsVariantIds || []).forEach(function (v) { allGets[String(v)] = true; });
    });
    if (!Object.keys(allGets).length) return; // no free items configured

    var desiredByVariant = {};
    Object.keys(allGets).forEach(function (v) { desiredByVariant[v] = 0; });

    discounts.forEach(function (d) {
      var getsVids = d.getsVariantIds || [];
      if (!getsVids.length) return;
      var pset = {};
      (d.productIds || []).forEach(function (id) { pset[String(id)] = true; });
      var allScope = d.allItems === true;

      var eligUnits = 0, eligCents = 0;
      items.forEach(function (it) {
        var vid = String(it.variant_id || it.id);
        if (allGets[vid]) return; // a free item never counts as an eligible buy
        if (allScope || pset[String(it.product_id)]) {
          eligUnits += it.quantity;
          eligCents += (it.original_line_price != null ? it.original_line_price : it.line_price);
        }
      });

      // Buy condition must be satisfied or the discount won't zero the free item.
      var ok = eligUnits > 0;
      if (d.minSpend != null) ok = ok && (eligCents >= Math.round(d.minSpend * 100));
      if (d.minQty != null) ok = ok && (eligUnits >= d.minQty);

      var desired = 0;
      if (ok) {
        var cap = (d.maxUses != null && d.maxUses > 0) ? d.maxUses : eligUnits;
        desired = Math.min(eligUnits, cap); // one free per eligible unit, capped at max uses
      }
      var vid0 = String(getsVids[0]);
      desiredByVariant[vid0] = Math.max(desiredByVariant[vid0] || 0, desired);
    });

    var currentByVariant = {};
    items.forEach(function (it) {
      var vid = String(it.variant_id || it.id);
      if (allGets[vid]) currentByVariant[vid] = (currentByVariant[vid] || 0) + it.quantity;
    });

    var changed = false;
    var vids = Object.keys(desiredByVariant);
    for (var i = 0; i < vids.length; i++) {
      var vid = vids[i];
      var want = desiredByVariant[vid];
      var have = currentByVariant[vid] || 0;
      if (want === have) continue;
      var done;
      if (have === 0 && want > 0) {
        done = await self._cartAddVariant(vid, want);
      } else {
        done = await self._cartSetVariantQty(vid, want); // want may be 0 → removes
      }
      if (done) changed = true;
    }
    // Re-render the cart with the corrected free items. Idempotent: after this,
    // desired === current, so the reloaded page makes no further change.
    if (changed) window.location.reload();
  } catch (e) {
    // Non-fatal — leave the cart untouched rather than risk a bad write.
  }
};

RewardStudio._cartAddVariant = function (variantId, qty) {
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty }] })
  }).then(function (r) { return r.ok; }).catch(function () { return false; });
};

RewardStudio._cartSetVariantQty = function (variantId, qty) {
  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ id: String(variantId), quantity: qty })
  }).then(function (r) { return r.ok; }).catch(function () { return false; });
};

// Apply the stored gift card balance to any [data-gift-card-price] element on the page.
// Each such element should have data-price-cents="<integer cents>" and contain a child
// with [data-gift-card-amount] that will receive the computed remainder.
// Note: the math assumes single-item purchase (price − card balance). For multi-item
// carts the cart page shows the accurate combined math; per-item display is illustrative.
RewardStudio.applyGiftCardToPrices = function () {
  var data = this.giftCard.get();
  if (!data || !data.balance) return;
  var balance = data.balance;
  var self = this;

  // Per-item "after card" pill — and any related visibility-only elements (footnotes
  // etc.) that share the [data-gift-card-price] hook but don't carry a price value.
  // When a price IS present, compute the after-card amount; in all cases, unhide
  // the element so it surfaces once a card is applied.
  document.querySelectorAll('[data-gift-card-price]').forEach(function (el) {
    var priceCents = parseInt(el.dataset.priceCents, 10);
    if (!isNaN(priceCents)) {
      var amountEl = el.querySelector('[data-gift-card-amount]');
      if (amountEl) {
        var remainder = priceCents - balance;
        // Fully-covered state (after-card price hits $0.00). Marketing default
        // is the numeric "$0.00"; a merchant can override with branded text
        // (e.g. "Covered") via Theme settings → Price layout labels →
        // "Fully-covered label", exposed as config.coveredLabel by theme.liquid.
        var coveredLabel = (self.config && self.config.coveredLabel) ? self.config.coveredLabel : '';
        amountEl.textContent = remainder > 0
          ? self.formatMoney(remainder)
          : (coveredLabel || self.formatMoney(0));
      }
      // Gift card CREDIT applied to this item = min(balance, price). Used by the
      // PDP "SAVE $X with your gift card" line (#134). Distinct from the total
      // markdown savings ([data-save-amount]); this is the card's contribution only.
      var creditEl = el.querySelector('[data-gift-card-credit]');
      if (creditEl) {
        creditEl.textContent = self.formatMoney(Math.min(balance, priceCents));
      }
    }
    el.removeAttribute('hidden');
  });

  // "Save $X" bubble — combines the manufacturer markdown with the gift card.
  // Total savings = compare_at_price − effective_price, where effective_price
  // is max(0, price − card balance). Single-item perspective; multi-item carts
  // show the real combined math on /cart.
  document.querySelectorAll('[data-price-save]').forEach(function (el) {
    var priceCents = parseInt(el.dataset.priceCents, 10);
    var compareCents = parseInt(el.dataset.compareCents, 10);
    if (isNaN(priceCents) || isNaN(compareCents) || compareCents <= priceCents) return;
    var effective = Math.max(0, priceCents - balance);
    var totalSavings = compareCents - effective;
    var amountEl = el.querySelector('[data-save-amount]');
    if (amountEl) amountEl.textContent = self.formatMoney(totalSavings);
  });
};

// Grid / List view toggle for product listing pages (Shop Products + brand pages).
// Reads every [data-view-toggle] control, toggles `.product-grid--list` on every
// [data-product-grid] container, and persists the choice in localStorage so it
// sticks across pages. Default = grid. No-ops if the page has no toggle/grid.
(function productView() {
  document.addEventListener('DOMContentLoaded', function () {
    var KEY = 'rs-product-view';
    var toggles = document.querySelectorAll('[data-view-toggle]');
    var grids = document.querySelectorAll('[data-product-grid]');
    if (!toggles.length || !grids.length) return;

    function apply(view) {
      var isList = view === 'list';
      grids.forEach(function (g) { g.classList.toggle('product-grid--list', isList); });
      toggles.forEach(function (t) {
        t.querySelectorAll('[data-view]').forEach(function (b) {
          var on = b.dataset.view === view;
          b.classList.toggle('active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      });
    }

    var saved = 'grid';
    try { saved = localStorage.getItem(KEY) || 'grid'; } catch (_e) {}
    apply(saved === 'list' ? 'list' : 'grid');

    toggles.forEach(function (t) {
      t.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-view]');
        if (!btn) return;
        var view = btn.dataset.view === 'list' ? 'list' : 'grid';
        try { localStorage.setItem(KEY, view); } catch (_e) {}
        apply(view);
      });
    });
  });
})();

// Mobile nav drawer — hamburger opens a slide-out panel (markup in header.liquid).
// Handles open/close via the toggle, the close button, the backdrop, and ESC.
// Locks body scroll while open and returns focus to the toggle on close.
(function navDrawer() {
  document.addEventListener('DOMContentLoaded', function () {
    var toggle = document.querySelector('[data-nav-toggle]');
    var drawer = document.querySelector('[data-nav-drawer]');
    var backdrop = document.querySelector('[data-nav-backdrop]');
    var closeBtn = document.querySelector('[data-nav-close]');
    if (!toggle || !drawer || !backdrop) return;

    function open() {
      drawer.classList.add('open');
      backdrop.classList.add('open');
      document.body.classList.add('nav-drawer-active');
      drawer.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      drawer.classList.remove('open');
      backdrop.classList.remove('open');
      document.body.classList.remove('nav-drawer-active');
      drawer.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }

    toggle.addEventListener('click', open);
    backdrop.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Close when a drawer link is followed, and on Escape.
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('a')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('open')) close();
    });

    // If the viewport grows back past the mobile breakpoint while open, reset.
    window.addEventListener('resize', function () {
      if (window.innerWidth > 768 && drawer.classList.contains('open')) close();
    });
  });
})();

// Preserve any pre-existing window.RewardStudio.config (set by theme.liquid's
// inline head script with the simulateGiftCard theme setting) before assigning.
// Otherwise this assignment wipes the config and lookupGiftCard always falls
// back to simulation regardless of the Theme settings checkbox.
if (window.RewardStudio && window.RewardStudio.config) {
  RewardStudio.config = window.RewardStudio.config;
}
window.RewardStudio = RewardStudio;
